import React, { useContext, useEffect, useState, useTransition } from 'react';
import { ItemModal } from '../item';
import { Helmet } from 'react-helmet-async';
import { RouterContext } from './RouterContext';
import { PreferenceContext, RequestContext, UserContext } from './';
import { currentDeviceWidth } from '../../lib/helpers';
import { pushToDataLayer } from 'services/datalayer';
import { useDidomiConsent } from '../common/hooks';

export const AppContext = React.createContext({});
AppContext.displayName = 'AppContext';

const initialBreakPoint = (userAgent) => {
  if (userAgent.isMobile) {
    return 'MOBILE';
  }
  if (userAgent.isTablet) {
    return 'TABLET';
  }
  if (userAgent.isDesktop) {
    return 'DESKTOP';
  }
};

const Component = () => <></>;
export const AppContextProvider = ({ children }) => {
  const {
    search,
    isModalActive,
    isDetailPage,
    pagePath: { activeList, pageTitle },
  } = useContext(RouterContext);
  const { userAgent } = useContext(RequestContext);
  const { user } = useContext(UserContext);
  const {
    preference: { nsfwActive, filter },
  } = useContext(PreferenceContext);
  const didomiConsent = useDidomiConsent();

  const [listPages, setListPages] = useState({
    activeList,
    pageNums: {
      [activeList]: [0],
    },
  });

  const [slideIn, setSlideIn] = useState({
    active: isModalActive,
    fullwidth: isModalActive,
    Component: isModalActive ? ItemModal : Component,
  });
  const [imageSlider, setImageSlider] = useState({ active: false, selected: 0, images: [] });
  const [soundBoardActive, setSoundBoardActive] = useState(false);
  const [twitchLiveStream, setTwitchLiveStream] = useState(false);
  const setTransition = useTransition()[1];
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0,
    adBreakPoint: initialBreakPoint(userAgent),
  });

  useEffect(() => {
    if (isModalActive) {
      setTransition(() => {
        setSlideIn((prevState) => ({
          ...prevState,
          active: true,
          fullwidth: true,
          Component: ItemModal,
        }));
      });
    } else {
      setTransition(() => {
        setSlideIn({
          fullwidth: false,
          active: false,
          Component,
        });
      });
    }
  }, [search, isModalActive]);

  useEffect(() => {
    setTransition(() => {
      const onResize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const adBreakPoint = userAgent.isMobile
          ? windowSize.adBreakPoint
          : currentDeviceWidth(width);
        setWindowSize({
          width,
          height,
          adBreakPoint,
        });
      };

      onResize();
      userAgent.isMobile
        ? window.addEventListener('orientationchange', onResize)
        : window.addEventListener('resize', onResize);
      return () => {
        userAgent.isMobile
          ? window.removeEventListener('orientationchange', onResize)
          : window.removeEventListener('resize', onResize);
      };
    });
  }, []);

  useEffect(() => {
    setTransition(() => {
      setListPages({
        ...listPages,
        activeList,
        pageNums: {
          [activeList]: [0],
        },
      });
    });
  }, [nsfwActive, filter]);

  useEffect(() => {
    setTransition(() => {
      setListPages({
        ...listPages,
        activeList,
        pageNums: {
          ...listPages.pageNums,
          [activeList]: listPages.pageNums[activeList] || [0],
        },
      });
      if (pageTitle && didomiConsent && didomiConsent.hasConsent) {
        pushToDataLayer(
          { title: pageTitle },
          user !== null,
          false,
          didomiConsent.consentStatusForAll.purposes.disabled,
        );
      }
    });
  }, [activeList]);

  return (
    <AppContext.Provider
      value={{
        slideIn,
        setSlideIn,
        imageSlider,
        setImageSlider,
        twitchLiveStream,
        setTwitchLiveStream,
        listPages,
        setListPages,
        soundBoardActive,
        setSoundBoardActive,
        windowSize,
        isDetailPage,
        activeList,
      }}
    >
      <Helmet>
        <meta
          name='viewport'
          content={`width=device-width, initial-scale=1${
            ['MOBILE'].includes(windowSize.adBreakPoint)
              ? ', maximum-scale=1, user-scalable=no'
              : ', maximum-scale=5'
          }`}
        />
      </Helmet>
      {children}
    </AppContext.Provider>
  );
};
